import { useFormik } from "formik";
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import * as yup from "yup";
import { useTranslation } from "react-i18next";

import useUserData from "../user/useUserData";
import { handleAxiosErrors } from "../../helpers/errorHandling";
import { enqueueSnackbar } from "notistack";
import { API } from "../../helpers/constants";
import { PAGE_LOGIN } from "../../locales/pages/namespaces";

export const useAccountData = () => {
    const [loading, setLoading] = useState(false);
    const [userData, setUserData] = useState(null);

    const { token } = useUserData();
    const { t } = useTranslation(PAGE_LOGIN);

    useEffect(() => {
        if (!token) return;

        setLoading(true);

        axios({
            method: "get",
            url: `${API}/en/brew/profile/data`,
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
        })
            .then((res) => {
                setUserData(res.data?.data ?? null);
                setLoading(false);
            })
            .catch((error) => {
                setLoading(false);
                enqueueSnackbar(handleAxiosErrors(error, t("An error occurred!")), {
                    variant: "error",
                    autoHideDuration: 3000,
                });
            });
    }, [token, t]);

    const initialValues = useMemo(() => {
        const user = userData?.user ?? {};

        return {
            name: user.LoginName ?? user.name ?? "",
            company: user.company ?? "",
            country: String(user.CountryId ?? user.country ?? ""),
            phone: user.BusinessPhone ?? user.phone ?? "",
            email: user.email ?? "",
            afm: user.afm ?? "",
            password: "",
            confirmPassword: "",
            currentPassword: "",
            newsletterOptIn: Boolean(user.newsletterOptIn),
            promotionsOptIn: Boolean(user.promotionsOptIn),
            terms: Boolean(user.terms),
        };
    }, [userData]);

    const onSubmit = (values) => {
        setLoading(true);

        axios({
            method: "post",
            url: `${API}/en/brew/profile/update`,
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            data: {
                email: values.email,
                password: values.password,
                currentPassword: values.currentPassword,
                name: values.name,
                company: values.company,
                country: values.country,
                phone: values.phone,
                afm: values.afm,
                newsletterOptIn: values.newsletterOptIn,
                promotionsOptIn: values.promotionsOptIn,
                terms: values.terms,
            },
        })
            .then(() => {
                enqueueSnackbar(t("User was successfully submitted!"), {
                    variant: "success",
                    autoHideDuration: 3000,
                });
                setLoading(false);
            })
            .catch((error) => {
                setLoading(false);
                enqueueSnackbar(handleAxiosErrors(error, t("An error occurred!")), {
                    variant: "error",
                    autoHideDuration: 3000,
                });
            });
    };

    const validationSchema = yup.object().shape({
        name: yup.string().required(t("Required")),
        country: yup.string().required(t("Required")),
        phone: yup
            .string()
            .required(t("Required"))
            .matches(/^[0-9]{9,10}$/, t("Invalid phone number")),
        email: yup
            .string()
            .required(t("Required"))
            .email(t("Invalid email")),
        afm: yup
            .string()
            .required(t("Required"))
            .matches(/^[0-9]{8,10}$/, t("Invalid AFM")),
        password: yup.string().test(
            "password-validation",
            t("Password must be between 6-20 characters and contain at least 3 of the following. -Upper case character -Lower case character -Numeric digit -Special character"),
            function (value) {
                if (!value) return true;

                return /^.{60}$|(^((?=.*?[A-Z])(?=.*?[a-z])(?=.*?\d)|(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[^a-zA-Z0-9])|(?=.*?[A-Z])(?=.*?\d)(?=.*?[^a-zA-Z0-9])|(?=.*?[a-z])(?=.*?\d)(?=.*?[^a-zA-Z0-9])).{6,20}$)/.test(
                    value
                );
            }
        ),
        currentPassword: yup.string().test(
            "current-password-validation",
            t("Password must be between 6-20 characters and contain at least 3 of the following. -Upper case character -Lower case character -Numeric digit -Special character"),
            function (value) {
                if (!value) return true;

                return /^.{60}$|(^((?=.*?[A-Z])(?=.*?[a-z])(?=.*?\d)|(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[^a-zA-Z0-9])|(?=.*?[A-Z])(?=.*?\d)(?=.*?[^a-zA-Z0-9])|(?=.*?[a-z])(?=.*?\d)(?=.*?[^a-zA-Z0-9])).{6,20}$)/.test(
                    value
                );
            }
        ),
        confirmPassword: yup.string().test(
            "confirm-password-validation",
            t("Passwords must match"),
            function (value) {
                const { password } = this.parent;

                if (!password && !value) return true;
                return value === password;
            }
        ),
    });

    const formik = useFormik({
        enableReinitialize: true,
        initialValues,
        validationSchema,
        onSubmit,
    });

    const countryOptions = useMemo(() => {
        return (userData?.countries ?? []).map((country) => ({
            value: String(country.id),
            label: country.Description ?? "",
        }));
    }, [userData]);



    return {
        formik,
        loading,
        countryOptions,
    };
};